import { canEditRecord } from '../../lib/recordAccess';
import { Alert, Button, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import { Settings, Refresh, Close } from "@mui/icons-material";
import PaymentChip from "../Payments/PaymentChip";
import PaymentPanel from "../Payments/PaymentPanel";
import ManualPayment from "../Payments/ManualPayment";
import { paymentsApi } from "../../lib/paymentsApi";
import { Construction, Delete } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RecordModal from "../RecordModal/RecordModal";
import { FormProvider } from "../../contexts/CreateRecordFormContext";
import OpportunityLineColumns from "./OpportunityLineColumns";
import { API } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import CollapsibleGridCard from "../CollapsibleGridCard";
import { getForecastMarginProductGroups, useAppSettings } from "../../contexts/AppSettingsContext";

const OpportunityLines = ({ setFeedback, project, setLogs, setProject }) => {
    const opportunityId = project?._id;
    const paymentSequence = useRef(0);
    const [paymentData, setPaymentData] = useState(null);
    const [paymentConfig, setPaymentConfig] = useState(null);
    const [paymentError, setPaymentError] = useState('');
    const [paymentLine, setPaymentLine] = useState(null);
    const refreshPayments = useCallback(async () => {
        if (!opportunityId) return;
        const sequence = ++paymentSequence.current;
        try {
            const [data, config] = await Promise.all([paymentsApi.revenue(opportunityId), paymentsApi.settings().catch(() => null)]);
            if (sequence !== paymentSequence.current) return;
            setPaymentData(data); setPaymentConfig(config); setPaymentError('');
            setRows(previous => previous.map(row => data.byLine?.[row._id]?.bookedOrdersStage === undefined ? row : { ...row, bookedOrdersStage: data.byLine[row._id].bookedOrdersStage }));
        } catch (e) { if (sequence === paymentSequence.current) { setPaymentConfig(null); setPaymentError(e.message); } }
    }, [opportunityId]);
    useEffect(() => {
        setPaymentData(null); setPaymentConfig(null); setPaymentError(''); setPaymentLine(null);
    }, [opportunityId]);
    useEffect(() => {
        refreshPayments();
        const timer = setInterval(() => { if (!document.hidden) refreshPayments(); }, 30000);
        return () => { clearInterval(timer); paymentSequence.current += 1; };
    }, [refreshPayments, project]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [rows, setRows] = useState([]);

    const { user } = useAuth();
    const canEdit = canEditRecord(user, project);
    const { applicationSettings } = useAppSettings();
    const role = String(user?.role || localStorage.getItem("role") || "").trim().toLowerCase();
    const productGroupOptions = useMemo(() => getForecastMarginProductGroups(applicationSettings), [applicationSettings]);
    const canEditProductGroup = role === "admin" || role === "manager";
    const canEditAmount = role === "admin";
    const canEditBookedOrdersStage = role === "admin" || role === "manager";
    const canEditShipDate = canEdit;
    const canDeleteLines = canEdit;
    const columns = [...OpportunityLineColumns({ canEditAmount, canEditBookedOrdersStage, canEditShipDate }),
        { field: 'paymentStatus', headerName: 'Payment Status', minWidth: 170, flex: 1,
          valueGetter: (_, row) => paymentData?.byLine?.[row._id]?.status || 'Not paid',
          renderCell: ({ row }) => <PaymentChip status={paymentData?.byLine?.[row._id]?.status || 'Not paid'} onClick={() => setPaymentLine(row)} /> },
        { field: 'paymentRequest', headerName: 'Payment Request', width: 210, sortable: false, filterable: false,
          renderCell: ({ row }) => {
            const reason = !canEdit ? 'Only the owner can modify this record' : !paymentConfig?.enabled ? 'Contact system admin to enable' : !paymentConfig.ready ? 'Connect Stripe in Payment settings'
              : !paymentData?.quoteId ? 'Link a quote first' : paymentData.unallocated ? 'Existing quote payments need allocation'
              : paymentData.byLine?.[row._id]?.hasActive ? 'An outstanding request exists. Click the status to view it.'
              : !paymentData.byLine?.[row._id]?.balance ? 'This revenue line is paid' : '';
            return <Tooltip title={reason}><span><Button size="small" disabled={Boolean(reason)} onClick={() => setPaymentLine({ ...row, requestNow: true })}>Send payment request</Button></span></Tooltip>;
          } },
    ];
    const updateFields = useMemo(() => {
        const fields = [];

        if (canEditProductGroup) {
            fields.push({
                name: "productGroup",
                label: "Product Group",
                type: "autocomplete",
                options: productGroupOptions,
            });
        }

        if (canEditAmount) {
            fields.push({ name: "amount", label: "Amount", type: "number" });
        }

        if (canEditBookedOrdersStage) {
            fields.push({
                name: "bookedOrdersStage",
                label: "Booked Orders Stage",
                type: "autocomplete",
                options: ['01 - Assign Resources', '02 - Submittals Sent', '03 - Submittals Approved', '04 - Shipped', '05 - Installed', '06 - Commissioned', '07 - Revenue Taken', '10 - Closed'],
            });
        }

        if (canEditShipDate) {
            fields.push({ name: "shipDate", label: "Ship Date", type: "date" });
        }

        return fields;
    }, [canEditAmount, canEditBookedOrdersStage, canEditProductGroup, canEditShipDate, productGroupOptions]);

    const handleSelectionChange = (selection) => setSelectedRows(selection)

    const handleOpenUpdateModal = () => selectedRows.length ? setOpenUpdateModal(true) : setFeedback({open: true, message: "Select at least one record to update!", type: "info"})
    const handleCloseUpdateModal = () => setOpenUpdateModal(false);

    const handleDeleteSelectedLines = async () => {
        if (!selectedRows.length) {
            setFeedback({ open: true, message: "Select at least one revenue line to delete!", type: "info" });
            return;
        }

        if (!canDeleteLines) {
            setFeedback({ open: true, message: "Only admins can delete revenue lines.", type: "error" });
            return;
        }

        const confirmDelete = window.confirm(`Delete ${selectedRows.length} selected revenue line(s)?`);
        if (!confirmDelete) return;

        const response = await API.Lines.Delete(project._id, selectedRows);

        if (response.status === 200) {
            refreshPayments();
            const deletedIds = response.data?.deletedIds || selectedRows;
            const deletedIdSet = new Set(deletedIds.map(String));
            const updatedOpportunity = response.data?.data?.[0];

            setRows(prevRows => prevRows.filter((line) => !deletedIdSet.has(String(line._id))));
            setProject(prevProject => {
                if (!prevProject) return prevProject;

                return {
                    ...prevProject,
                    ...(updatedOpportunity ? { amount: updatedOpportunity.amount } : {}),
                    lines: (prevProject.lines || []).filter((line) => !deletedIdSet.has(String(line?._id || line))),
                };
            });

            if (response.data?.logs?.length) {
                setLogs(prevLogs => [...response.data.logs, ...(prevLogs || [])]);
            }

            setSelectedRows([]);
            setFeedback({ open: true, message: response.data.message, type: "success" });
        } else {
            setFeedback({ open: true, message: response?.data?.message || "Error deleting revenue line(s)...", type: "error" });
        }
    };

    const handleUpdateSubmit = async (updateData) => {
        let temp = []
        
        let updatedFields = {}
        Object.keys(updateData).forEach(key => {
            if (updateData[key] !== "" && updateData[key] != null) {
                Object.assign(updatedFields, { [key]: updateData[key] } )
            }
        });

        if (!Object.keys(updatedFields).length) {
            setFeedback(prevData => ({ ...prevData, open: true, message: "Choose at least one allowed field to update.", type: "info" }))
            return
        }
        
        for (let i=0; i<selectedRows.length; i++) {
            temp.push({_id: selectedRows[i], data: updatedFields })
        }
        
        const response = await API.Lines.UpdateMulti(project._id, temp)

        if (response.status === 200) {
            const updatedById = new Map((response.data?.data || []).map((line) => [String(line._id), line]));
            setRows(prevLines =>
                prevLines.map((line) => updatedById.get(String(line._id)) || line)
            );
            setFeedback(prevData => ({...prevData, open: true, message: response.data.message, type: "success" }))
        } else {
            console.log(response)
            setFeedback(prevData => ({ ...prevData, open: true, message: response?.data?.message || 'Error updating line(s)...', type: "error" }))
        }
        
        handleCloseUpdateModal(false)         
    }

    const actionButtons = [
        { title: "Payment settings", icon: Settings, onClick: () => window.location.assign("/settings") },
        { title: "Refresh payments", icon: Refresh, onClick: refreshPayments },
        { title: "Update Selected Record(s)", onClick: handleOpenUpdateModal, disabled: !canEdit, icon: Construction, color: "primary" },
        ...(canDeleteLines
            ? [{ title: "Delete Selected Revenue Line(s)", onClick: handleDeleteSelectedLines, icon: Delete, color: "error" }]
            : []),
    ];

    const handleProcessRowUpdate = async (newRow, oldRow) => {
        try {
            const updatedFields = {};

            // Compare fields and detect what changed
            for (const key in newRow) {
                if (newRow[key] !== oldRow[key]) {
                    updatedFields[key] = newRow[key];
                }
            }
    
            if (Object.keys(updatedFields).length === 0) {
                return oldRow; // nothing changed
            }

            const response = await API.Lines.UpdateSingle(newRow._id, project._id, updatedFields) 

            if (response.status === 200) {
                setRows(prevRows =>
                    prevRows.map((row) =>
                        row._id === newRow._id ? { ...row, ...(response.data?.data || updatedFields) } : row
                    )
                );
                setFeedback({open: true, message: `${newRow.productGroup} updated successfully!`, type: "success"})
                return response.data?.data || newRow;
            } else {
                setFeedback({open: true, message: response?.data?.message || `${newRow.productGroup} failed to update...`, type: "error"})
                return oldRow;
            }
        } catch (error) {
            setFeedback({open: true, message: `${newRow.productGroup} failed to update...`, type: "error"})
            return oldRow;
        }
    };

    useEffect(() => {
        project && setRows(project?.lines)
        // async function getData() {
        //     const response = await API.Lines.FetchByOpportunity(oid, localStorage.token)
        //     setRows(response)
        // }
        // getData()
    },[project])

    return (
        <FormProvider>
            {paymentError && <Alert severity="error">{paymentError}</Alert>}
            {paymentData?.unallocated && <Alert severity="warning">Existing quote-level payments need allocation before requesting payments for individual revenue lines. Contact system admin. <Button onClick={() => setPaymentLine({ _id: "legacy", legacy: true, lineComments: "Previous quote payments" })}>Review previous payments</Button></Alert>}
            <Dialog open={Boolean(paymentLine)} onClose={() => setPaymentLine(null)} maxWidth="md" fullWidth PaperProps={{ sx: { maxWidth: 760, borderRadius: 3, m: { xs: 1.5, sm: 4 }, width: 'calc(100% - 24px)' } }}>
                <DialogTitle sx={{ px: 3, pt: 2.5, pb: 2.5, fontSize: 20, fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>{paymentLine?.lineComments || paymentLine?.productGroup || "Revenue line"} {paymentLine && !paymentLine.legacy && <PaymentChip sx={{ ml: 1.5, verticalAlign: "middle" }} status={paymentData?.byLine?.[paymentLine._id]?.status || "Not paid"} />}<IconButton aria-label="Close payments" onClick={() => setPaymentLine(null)} sx={{ float: "right" }}><Close /></IconButton></DialogTitle>
                <DialogContent sx={{ p: 3, '&.MuiDialogContent-root': { pt: 3 } }}>
                    {paymentLine && !paymentLine.legacy && !paymentLine.requestNow && <ManualPayment readOnly={!canEdit} key={paymentLine._id} opportunityId={opportunityId} lineId={paymentLine._id} summary={paymentData?.byLine?.[paymentLine._id]} onSaved={refreshPayments} />}
                    {paymentLine && paymentData?.quoteId && <PaymentPanel readOnly={!canEdit} key={paymentLine._id} quoteId={paymentData.quoteId} lineId={paymentLine.legacy ? undefined : paymentLine._id} compact hideSummary={!paymentLine.legacy && !paymentLine.requestNow} autoRequest={!!paymentLine.requestNow} historyOnly={!!paymentLine.legacy} onChanged={refreshPayments} version={paymentData?.byLine?.[paymentLine._id]?.manualPaid} />}
                </DialogContent>
            </Dialog>
            <RecordModal open={openUpdateModal} handleClose={handleCloseUpdateModal} fields={updateFields} title={"Update Revenue Line(s)"} onSubmit={handleUpdateSubmit} />
            <CollapsibleGridCard
                title="Revenue Lines"
                headerRightNode={<PaymentChip status={paymentData?.status || "Not paid"} />}
                linkLabel={project?.name}
                className="col-xl-12 col-md-12"
                variantClassName="hive-single-quote-lines-card"
                gridClassName="hive-single-quote-lines-grid"
                actionButtons={actionButtons}
                rows={rows}
                columns={columns}
                getRowId={(row) => row._id}
                checkboxSelection
                rowSelectionModel={selectedRows}
                onRowSelectionModelChange={handleSelectionChange}
                extraGridProps={{
                    processRowUpdate: handleProcessRowUpdate,
                    experimentalFeatures: { newEditingApi: true },
                }}
            />
        </FormProvider>
    )
}

export default OpportunityLines
